import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  AI_ASSISTANT_GENERATOR,
  AI_ASSISTANT_VERSIONING,
  AI_DRAFT_PREVIEW_ENABLED,
  AI_SUCCESS_EVALUATORS,
  ASSISTANT_SITUATION_CONFIG,
  CONTENT_CREATOR,
  CUSTOM_ASSISTANT_PLACEHOLDER_ID,
  SYSTEM_ASSISTANT_ID,
  WEBRTC_VOICE,
} from '../ai-assistant.constants';
import {
  AiAssistant,
  AiConnection,
  ConnectionType,
  CTAction,
  MY_LISTING_CONNECTION_NAME,
} from '../interfaces/ai-assistant.interface';

import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  ApplyAssistantUpdatesRequest,
  ApplyAssistantUpdatesResponse,
  Assistant,
  AssistantKey,
  AssistantType,
  AssistantVersionKey,
  AssistantVersionSummary,
  ConstraintFilter,
  Connection,
  Function as ProtoFunction,
  Goal,
  Model,
  ModelType,
  Namespace,
  PromptModule,
  PromptModuleKey,
  UpsertAssistantRequest,
  UpsertAssistantResponse,
  VersionType,
  listAvailableModels$,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  applyAssistantUpdates$ as applyAssistantUpdatesProto$,
  buildDefaultAssistant$ as buildDefaultAssistantProto$,
  deleteAssistant$ as deleteAssistantProto$,
  getAssistant$ as getAssistantProto$,
  getAssistantVersion$ as getAssistantVersionProto$,
  listAllAssistantsAssociatedToConnection$ as listAllAssistantsAssociatedToConnectionProto$,
  listAssistant$ as listAssistantProto$,
  listAssistantVersions$ as listAssistantVersionsProto$,
  upsertAssistant$ as upsertAssistantProto$,
} from '@vendasta/protos/ai_assistants/v1alpha1/assistant-service';
import {
  get$ as getFunctionProto$,
  list$ as listFunctionsProto$,
  upsert$ as upsertFunctionProto$,
} from '@vendasta/protos/ai_assistants/v1alpha1/function-service';
import {
  deleteConnection$ as deleteConnectionProto$,
  listConnections$ as listConnectionsProto$,
  setConnectionAssistant$ as setConnectionAssistantProto$,
  upsertConnection$ as upsertConnectionProto$,
} from '@vendasta/protos/ai_assistants/v1alpha1/connection-service';
import {
  create$ as createGoal$,
  get$ as getGoal$,
  upsert$ as upsertGoal$,
} from '@vendasta/protos/ai_assistants/v1alpha1/goal-service';
import {
  create$ as createPromptModule$,
  createVersion$ as createPromptModuleVersion$,
  getDeployedVersion$ as getDeployedPromptModuleVersion$,
} from '@vendasta/protos/ai_assistants/v1alpha1/prompt-module-service';
import { catchError, combineLatest, filter, firstValueFrom, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  AI_DEFAULT_CONNECTIONS_TOKEN,
  AI_DEFAULT_WORKFORCE_TOKEN,
  AI_MARKETER_AVAILABLE_TOKEN,
  MARKET_ID_TOKEN,
  NAMESPACE_CONFIG_TOKEN,
  PARTNER_ID_TOKEN,
  VOICE_AI_AVAILABLE_TOKEN,
} from '../tokens';

import { Router } from '@angular/router';
import { FeatureFlagService, FeatureFlagStatusInterface } from '@galaxy/partner';
import { isNotNullOrUndefined } from '@vendasta/rx-utils';
import { AssistantIdToAssistantType, getNamespace, globalNamespace } from './ai-assistant-utils';
import { URLBuilderService } from './url-builder.service';


@Injectable()
export class AiAssistantService {
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly marketId$ = inject(MARKET_ID_TOKEN);
  readonly defaultWorkforce$ = inject(AI_DEFAULT_WORKFORCE_TOKEN);
  private readonly namespaceConfig$ = inject(NAMESPACE_CONFIG_TOKEN);
  readonly defaultConnections$ = inject(AI_DEFAULT_CONNECTIONS_TOKEN);

  private readonly translateService = inject(TranslateService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private systemAssistantCache$: Observable<Partial<Assistant>> | null = null;
  private readonly urlBuilder = inject(URLBuilderService);

  private readonly featureFlags$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
    switchMap(([partnerId, marketId]) =>
      this.featureFlagService.batchGetStatus(partnerId, marketId, [
        ASSISTANT_SITUATION_CONFIG,
        CONTENT_CREATOR,
        WEBRTC_VOICE,
        AI_ASSISTANT_GENERATOR,
        AI_DRAFT_PREVIEW_ENABLED,
        AI_SUCCESS_EVALUATORS,
        AI_ASSISTANT_VERSIONING,
      ]),
    ),
    shareReplay(1),
  );
  private readonly featureFlags: Signal<FeatureFlagStatusInterface> = toSignal(this.featureFlags$, {
    initialValue: {},
  });

  public readonly isVoiceAIAvailable = toSignal(inject(VOICE_AI_AVAILABLE_TOKEN));
  public readonly isAIMarketerAvailable = toSignal(inject(AI_MARKETER_AVAILABLE_TOKEN));

  private readonly defaultWorkforce = toSignal(this.defaultWorkforce$);
  public readonly isVoiceReceptionistEnabled = computed(() => {
    const receptionist = this.defaultWorkforce()?.find(
      (assistant) => assistant.assistant.type === AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST,
    );
    return !!receptionist && !receptionist.isDisabled;
  });

  public readonly isContentCreatorEnabled = computed(() => this.featureFlags()[CONTENT_CREATOR]);
  public readonly isAssistantSituationConfigEnabled = computed(() => this.featureFlags()[ASSISTANT_SITUATION_CONFIG]);
  public readonly isWebrtcVoiceEnabled = computed(() => this.featureFlags()[WEBRTC_VOICE]);
  public readonly isAssistantGeneratorEnabled = computed(() => this.featureFlags()[AI_ASSISTANT_GENERATOR]);
  public readonly isSuccessEvaluatorsEnabled = computed(() => this.featureFlags()[AI_SUCCESS_EVALUATORS]);
  public readonly isAssistantVersioningEnabled = computed(() => this.featureFlags()[AI_ASSISTANT_VERSIONING] ?? false);
  public readonly isAiDraftPreviewEnabled$: Observable<boolean> = this.featureFlags$.pipe(
    map((flags) => flags[AI_DRAFT_PREVIEW_ENABLED] ?? false),
  );

  activeConnectionsForAssistant(assistantId: string): Observable<AiConnection[]> {
    return this.namespaceConfig$.pipe(
      map((config) => config.namespace),
      switchMap((namespace) => this.listConnectionsForAssistant(namespace, assistantId)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getAssistant(partnerId: string, accountGroupId: string, assistantId: string): Observable<Partial<Assistant>> {
    const namespace = getNamespace(accountGroupId, partnerId);
    return getAssistantProto$({ id: assistantId, namespace }).pipe(
      map((resp) => resp.assistant),
      filter(isNotNullOrUndefined),
      catchError((err) => {
        console.error('Error getting AI assistant information', err);
        throw err;
      }),
    );
  }

  getGoal(goalId: string, namespace: Namespace): Observable<Partial<Goal> | undefined> {
    return getGoal$({ id: goalId, namespace }).pipe(
      map((resp) => resp.goal),
      catchError((err) => {
        console.error('Error getting AI goal', err);
        throw err;
      }),
    );
  }

  getSystemAssistant(): Observable<Partial<Assistant>> {
    if (this.systemAssistantCache$) {
      return this.systemAssistantCache$;
    }

    this.systemAssistantCache$ = getAssistantProto$({
      id: SYSTEM_ASSISTANT_ID,
      namespace: { systemNamespace: {} },
      options: { skipGoalsHydration: true },
    }).pipe(
      map((resp) => resp.assistant),
      filter(isNotNullOrUndefined),
      shareReplay(1),
      catchError((err) => {
        console.error('Error getting system AI assistant information', err);
        this.systemAssistantCache$ = null;
        throw err;
      }),
    );
    return this.systemAssistantCache$;
  }

  getHydratedAssistant(
    partnerId: string,
    accountGroupId: string,
    assistantId: string,
    options?: { skipGoalsHydration?: boolean },
  ): Observable<AiAssistant | null> {
    let assistant$: Observable<Partial<Assistant>>;
    if (assistantId === SYSTEM_ASSISTANT_ID) {
      if (options?.skipGoalsHydration === false) {
        assistant$ = getAssistantProto$({
          id: SYSTEM_ASSISTANT_ID,
          namespace: { systemNamespace: {} },
        }).pipe(
          map((resp) => resp.assistant),
          filter(isNotNullOrUndefined),
        );
      } else {
        assistant$ = this.getSystemAssistant();
      }
    } else {
      assistant$ = this.getAssistant(partnerId, accountGroupId, assistantId);
    }

    return combineLatest([assistant$, this.defaultWorkforce$]).pipe(
      map(([assistant, defaultWorkforce]) => this.hydrateAssistantWithDefaultInfo(assistant, defaultWorkforce)),
      catchError((err) => {
        console.warn('AI assistant not found, proceeding with default assistant info:', err);
        return of(null);
      }),
    );
  }

  listAllAssistants(namespace: Namespace, type?: AssistantType): Observable<Partial<Assistant>[]> {
    if (!namespace) {
      throw new Error('No account group or partner ID found');
    }

    const fetchAllPages = (cursor = '', accumulatedAssistants: Partial<Assistant>[] = []): Observable<Partial<Assistant>[]> => {
      return listAssistantProto$({
        filters: {
          type: type ?? AssistantType.ASSISTANT_TYPE_UNSPECIFIED,
          namespace,
        },
        pagingOptions: cursor ? { cursor } : undefined,
      }).pipe(
        switchMap((resp) => {
          const newAccumulatedAssistants = [...accumulatedAssistants, ...(resp.assistants ?? [])];

          if (resp.metadata?.hasMore && resp.metadata?.nextCursor) {
            return fetchAllPages(resp.metadata.nextCursor, newAccumulatedAssistants);
          }

          return of(newAccumulatedAssistants);
        }),
        catchError((e) => {
          console.error('Error getting AI workforce', e);
          return of(accumulatedAssistants);
        }),
      );
    };

    return fetchAllPages();
  }

  getDefaultAssistant(partnerId: string, accountGroupId: string, assistantId: string): Observable<AiAssistant> {
    const namespace = getNamespace(accountGroupId, partnerId);
    const type = AssistantIdToAssistantType(assistantId);

    return buildDefaultAssistantProto$({
      id: assistantId,
      namespace,
      type,
    }).pipe(
      map((resp) => ({ assistant: resp.assistant ?? {} })),
      catchError((err) => {
        throw new Error('Error getting default assistant', { cause: err });
      }),
    );
  }

  listConnections(namespace: Namespace, includeAssistantKeys: boolean): Observable<AiConnection[]> {
    return this.defaultConnections$.pipe(
      switchMap((defaultAiConnections) => {
        return listConnectionsProto$({
          filters: { namespace },
          includeAssistantKeys: includeAssistantKeys,
        }).pipe(
          map((resp): AiConnection[] => {
            const connections = resp?.connections ?? [];
            return connections.map((connection) => ({
              connection,
              isDefault: false,
              cta: this.buildConnectionCTAFromUrl(connection),
            }));
          }),
          map((aiConnections) => this.mergeWithDefaultConnections(aiConnections, defaultAiConnections)),
          catchError(() => {
            throw new Error('Error getting namespace connections');
          }),
        );
      }),
    );
  }

  private mergeWithDefaultConnections(
    apiConnections: AiConnection[],
    defaultConnections: AiConnection[] | undefined | null,
  ): AiConnection[] {
    if (!defaultConnections) return apiConnections;

    const enhancedConnections = apiConnections.map((apiConnection) => {
      const whiteLabelDefault = defaultConnections.find(
        (defaultConn) =>
          defaultConn.connection.connectionType === apiConnection.connection.connectionType &&
          defaultConn.connection.iconUrl,
      );

      if (whiteLabelDefault) {
        return {
          ...apiConnection,
          connection: {
            ...apiConnection.connection,
            iconUrl: whiteLabelDefault.connection.iconUrl,
          },
        };
      }
      return apiConnection;
    });

    const missingDefaults = defaultConnections.filter((defaultConnection) => {
      const exists = enhancedConnections.some((apiConnection) => {
        if (defaultConnection.connection.name === MY_LISTING_CONNECTION_NAME) {
          return apiConnection.connection.name === defaultConnection.connection.name;
        }
        return (
          apiConnection.connection.connectionType === defaultConnection.connection.connectionType ||
          apiConnection.connection.name === defaultConnection.connection.name
        );
      });
      return !exists;
    });

    return [...enhancedConnections, ...missingDefaults];
  }

  listConnectionsForAssistant(namespace: Namespace, assistantId: string): Observable<AiConnection[]> {
    return this.listConnections(namespace, true).pipe(
      map((aiConnections) => {
        const assistantType = AssistantIdToAssistantType(assistantId);

        // Only include connections that are associated with this assistant
        return aiConnections
          .filter((aiConnection) => {
            return aiConnection?.connection?.supportedAssistantTypes?.includes(assistantType);
          })
          .filter(isNotNullOrUndefined);
      }),
    );
  }

  // Personal email connections are per-mailbox (one ai-assistants Connection per Gmail/Outlook
  // mailbox, keyed by the Platform Integrations connection ID). There is no single fixed ID, so
  // callers enumerate the namespace's PersonalEmail connections and key off each connection's id.
  listPersonalEmailConnections(
    partnerId: string | undefined,
    accountGroupId: string | undefined,
  ): Observable<AiConnection[]> {
    const namespace = getNamespace(accountGroupId, partnerId);
    if (!namespace) {
      return of([]);
    }
    return this.listConnections(namespace, false).pipe(
      map((aiConnections) =>
        aiConnections.filter(
          (aiConnection) => aiConnection?.connection?.connectionType === ConnectionType.PersonalEmail,
        ),
      ),
    );
  }

  // Provision a per-mailbox PersonalEmail connection. The connection id is the Platform
  // Integrations connection id for the mailbox, so the draft path (which reads ConnectionID off
  // the inbound TrackedMessage) resolves the bound assistant. Idempotent — safe to re-call.
  upsertPersonalEmailConnection(
    partnerId: string | undefined,
    accountGroupId: string | undefined,
    connectionId: string,
    mailboxAddress: string,
  ): Observable<void> {
    const namespace = getNamespace(accountGroupId, partnerId);
    if (!namespace) {
      return throwError(() => new Error('cannot upsert a personal email connection without a partner or account group namespace'));
    }
    return upsertConnectionProto$({
      connection: {
        id: connectionId,
        namespace,
        name: mailboxAddress,
        connectionType: ConnectionType.PersonalEmail,
      },
    });
  }

  // Bind an assistant to a per-mailbox PersonalEmail connection (1:1 — replaces any existing
  // association). connectionId is the Platform Integrations connection id of the mailbox.
  setPersonalEmailConnectionAssistant(
    partnerId: string | undefined,
    accountGroupId: string | undefined,
    connectionId: string,
    assistantId: string,
  ): Observable<void> {
    const namespace = getNamespace(accountGroupId, partnerId);
    if (!namespace) {
      return throwError(() => new Error('cannot bind a personal email connection assistant without a partner or account group namespace'));
    }
    return setConnectionAssistantProto$({
      connectionKey: { id: connectionId, namespace, connectionType: ConnectionType.PersonalEmail },
      assistantKey: { id: assistantId, namespace },
    });
  }

  // Remove a per-mailbox PersonalEmail connection, stopping AI drafting for that mailbox. This
  // deletes the ai-assistants connection only; it does not disconnect the mailbox in Platform
  // Integrations. connectionId is the Platform Integrations connection id of the mailbox.
  deletePersonalEmailConnection(
    partnerId: string | undefined,
    accountGroupId: string | undefined,
    connectionId: string,
  ): Observable<void> {
    const namespace = getNamespace(accountGroupId, partnerId);
    if (!namespace) {
      return throwError(() => new Error('cannot delete a personal email connection without a partner or account group namespace'));
    }
    return deleteConnectionProto$({ id: connectionId, namespace, connectionType: ConnectionType.PersonalEmail });
  }

  listAllAssistantsAssociatedToConnection(
    partnerId: string | undefined,
    accountGroupId: string | undefined,
    connectionID: string,
    connectionType: ConnectionType,
    assistantType?: AssistantType,
  ): Observable<Partial<Assistant>[]> {
    if (!connectionID) {
      throw new Error('Connection ID is required');
    }
    return listAllAssistantsAssociatedToConnectionProto$({
      connectionKey: {
        id: connectionID,
        namespace: getNamespace(accountGroupId, partnerId),
        connectionType: connectionType,
      },
      filters: {
        type: assistantType,
      },
    }).pipe(
      map((resp) => resp.assistants ?? []),
      catchError(() => {
        throw new Error('Error getting assistants associated to connection');
      }),
    );
  }

  buildAiKnowledgeAppConfigurationUrl(assistantId: string): Observable<string> {
    return this.urlBuilder.aiKnowledgeAppConfigurationUrl(assistantId);
  }

  buildAppId(assistantId?: string): string {
    return assistantId ? `APP-AI${assistantId}` : '';
  }

  buildAssistantConfigurationUrl(assistantId: string, v2 = false): Observable<string> {
    return this.urlBuilder.assistantConfigurationUrl(assistantId, v2);
  }

  buildAssistantUrl(): Observable<string> {
    return this.urlBuilder.assistantUrl();
  }

  buildAiAssistantChatUrl(assistantId: string, conversationId?: string): Observable<string> {
    return this.urlBuilder.buildAiAssistantChatUrl(assistantId, conversationId);
  }

  isChatSupported(type: AssistantType | undefined): boolean {
    return (
      type === AssistantType.ASSISTANT_TYPE_INBOX ||
      type === AssistantType.ASSISTANT_TYPE_SALES_COACH ||
      type === AssistantType.ASSISTANT_TYPE_MARKETER ||
      type === AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT ||
      type === AssistantType.ASSISTANT_TYPE_REPUTATION_MANAGEMENT_REVIEW_AGENT ||
      ((type === AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING ||
        type === AssistantType.ASSISTANT_TYPE_BLOGGER) &&
        !!this.isContentCreatorEnabled())
    );
  }

  shouldShowChatButtonForAssistant(assistant: AiAssistant | null | undefined): boolean {
    const id = assistant?.assistant?.id;
    if (!id || id === CUSTOM_ASSISTANT_PLACEHOLDER_ID) return false;

    const isCustom = assistant.assistant.type === AssistantType.ASSISTANT_TYPE_CUSTOM;
    const supportsChatByType =
      !assistant.isDisabled && !assistant.isDefault && this.isChatSupported(assistant.assistant.type);

    return supportsChatByType || (isCustom && !assistant.isDisabled);
  }

  async navigateToAssistantChat(assistantId: string | undefined, router: Router): Promise<void> {
    if (!assistantId) return;
    const chatUrl = await firstValueFrom(this.buildAiAssistantChatUrl(assistantId));
    await router.navigateByUrl(chatUrl);
  }

  hydrateAssistantWithDefaultInfo(assistant: Partial<Assistant>, defaultAssistants: AiAssistant[]): AiAssistant | null {
    if (!assistant) return null;

    let defaultInfo: AiAssistant | undefined;
    if (assistant.type === AssistantType.ASSISTANT_TYPE_CUSTOM) {
      defaultInfo =
        defaultAssistants?.find(
          (defaultAssistant) => defaultAssistant.assistant.type === AssistantType.ASSISTANT_TYPE_CUSTOM,
        ) ?? this.getCustomAIAssistantTemplate();
    } else {
      defaultInfo = defaultAssistants?.find((defaultAssistant) => {
        return defaultAssistant.assistant.id === assistant.id;
      });
    }
    if (!defaultInfo) {
      return {
        assistant: assistant,
      };
    }
    const aiAssistant = this.cloneAssistant(defaultInfo);
    aiAssistant.isDefault = false;
    aiAssistant.isComingSoon = false;
    const defaultName = aiAssistant.assistant.name ?? '';
    const translatedName = this.translateService.instant(defaultName);

    let hydratedAssistant: Partial<Assistant> = assistant;
    if (!assistant.name) {
      hydratedAssistant = { ...assistant, name: translatedName };
    } else if (assistant.role) {
      aiAssistant.subtitleKey = assistant.role;
    } else if (translatedName.toLowerCase() !== assistant.name.toLowerCase()) {
      aiAssistant.subtitleKey = defaultName;
    }

    if (assistant.description) {
      aiAssistant.descriptionKey = assistant.description;
    }

    if (defaultInfo?.alertInformation) {
      aiAssistant.alertInformation = defaultInfo.alertInformation;
    }

    if (defaultInfo?.hideConnections) {
      aiAssistant.hideConnections = defaultInfo.hideConnections;
    }

    aiAssistant.assistant = hydratedAssistant;
    return aiAssistant;
  }

  getCustomAIAssistantTemplate(): AiAssistant {
    return {
      assistant: {
        name: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.TITLE',
        type: AssistantType.ASSISTANT_TYPE_CUSTOM,
      },
      descriptionKey: 'AI_ASSISTANT.WORKFORCE.DEFAULT_ASSISTANTS.CUSTOM_ASSISTANT.DESCRIPTION',
      decoration: {
        gradientColor: '#9066F7',
      },
    };
  }

  protected buildConnectionCTAFromUrl(connection: Partial<Connection>): CTAction {
    const configurationUrl = connection.configurationUrl;
    if (!configurationUrl) {
      return {};
    }

    return {
      label: 'AI_ASSISTANT.SETTINGS.SETTINGS',
      action: {
        url: configurationUrl,
        relativeRoute: !!connection.namespace?.accountGroupNamespace,
        showButton: true,
      },
    };
  }

  cloneAssistant(assistant: AiAssistant) {
    const fn = assistant.computeCTA;
    const ctaCallback =
      assistant.cta?.action && 'callback' in assistant.cta.action ? assistant.cta.action.callback : undefined;

    assistant.computeCTA = undefined;
    if (assistant.cta?.action && 'callback' in assistant.cta.action) {
      assistant.cta.action.callback = undefined;
    }

    const clonedAssistant = structuredClone(assistant);
    clonedAssistant.computeCTA = fn;
    assistant.computeCTA = fn;

    if (assistant.cta?.action && 'callback' in assistant.cta.action) {
      assistant.cta.action.callback = ctaCallback;
    }

    if (ctaCallback && clonedAssistant.cta?.action) {
      Object.assign(clonedAssistant.cta.action, { callback: ctaCallback });
    }

    return clonedAssistant;
  }

  async upsertPromptModule(promptModule: Partial<PromptModule>, content?: string): Promise<string> {
    const accountGroupId = await firstValueFrom(this.accountGroupId$);
    const partnerId = await firstValueFrom(this.partnerId$);
    const namespace = getNamespace(accountGroupId, partnerId);

    if (!promptModule.id) {
      const response = await firstValueFrom(
        createPromptModule$({
          namespace,
          name: promptModule.name,
          description: promptModule.description,
          content,
        }),
      );

      return response.id;
    } else {
      await firstValueFrom(
        createPromptModuleVersion$({
          id: promptModule.id,
          namespace,
          content,
          options: { shouldDeploy: true },
        }),
      );

      return promptModule.id;
    }
  }

  async getMultiPromptModuleVersions(keys: Partial<PromptModuleKey>[]): Promise<Record<string, string>> {
    // TODO: Once there is a getMultiDeployedVersion (without hydration), fetch this in one call

    const requests = keys.map((key) =>
      firstValueFrom(
        getDeployedPromptModuleVersion$({
          id: key.id,
          namespace: key.namespace,
        }).pipe(map((response) => [key.id, response.deployedPromptModuleVersion?.content ?? ''])),
      ),
    );
    const responses = await Promise.all(requests);
    return Object.fromEntries(responses);
  }

  getFunction(id: string): Observable<Partial<ProtoFunction> | undefined> {
    return combineLatest([this.partnerId$, this.accountGroupId$]).pipe(
      switchMap(([partnerId, accountGroupId]) =>
        getFunctionProto$({ id, namespace: getNamespace(accountGroupId, partnerId) }).pipe(
          map((resp) => resp.function),
        ),
      ),
    );
  }

  listFunctions(options?: {
    includeGlobal?: boolean;
    cursor?: string;
    pageSize?: number;
    constraintFilters?: Partial<ConstraintFilter>[];
    searchTerm?: string;
  }): Observable<{ functions: Partial<ProtoFunction>[]; nextCursor: string; hasMore: boolean }> {
    const { includeGlobal = false, constraintFilters, pageSize, searchTerm, cursor } = options || {};

    if (searchTerm) {
      return this.fetchAndFilterFunctions({ includeGlobal, constraintFilters, cursor, pageSize }, searchTerm);
    }

    return combineLatest([this.partnerId$, this.accountGroupId$]).pipe(
      switchMap(([partnerId, accountGroupId]) =>
        listFunctionsProto$({
          pagingOptions: {
            cursor: cursor ?? '',
            ...(pageSize && { pageSize }),
          },
          filters: {
            namespaces: [getNamespace(accountGroupId, partnerId), globalNamespace]
              .filter((ns): ns is Namespace => ns !== undefined)
              .filter((ns) => ns !== globalNamespace || includeGlobal),
            ...(constraintFilters && constraintFilters.length > 0 && { constraintFilters: constraintFilters as ConstraintFilter[] }),
          },
        }).pipe(
          map((res) => ({
            functions: res.functions ?? [],
            nextCursor: res.metadata?.nextCursor ?? '',
            hasMore: res.metadata?.hasMore ?? false,
          })),
        ),
      ),
    );
  }

  private fetchAndFilterFunctions(
    listOptions: {
      includeGlobal?: boolean;
      cursor?: string;
      pageSize?: number;
      constraintFilters?: Partial<ConstraintFilter>[];
    },
    searchTerm: string,
  ): Observable<{ functions: Partial<ProtoFunction>[]; nextCursor: string; hasMore: boolean }> {
    const pageSize = listOptions.pageSize || 25;
    const startIndex = listOptions.cursor ? parseInt(listOptions.cursor, 10) : 0;

    return this.fetchAllFunctions(listOptions).pipe(
      map((allFunctions) => {
        const searchTermLower = searchTerm.toLowerCase();
        const filteredFunctions = allFunctions.filter(
          (func) =>
            func.id?.toLowerCase().includes(searchTermLower) ||
            func.description?.toLowerCase().includes(searchTermLower),
        );

        const endIndex = startIndex + pageSize;
        const paginatedResults = filteredFunctions.slice(startIndex, endIndex);

        return {
          functions: paginatedResults,
          nextCursor: endIndex < filteredFunctions.length ? endIndex.toString() : '',
          hasMore: endIndex < filteredFunctions.length,
        };
      }),
    );
  }

  private fetchAllFunctions(listOptions: {
    includeGlobal?: boolean;
    constraintFilters?: Partial<ConstraintFilter>[];
  }): Observable<Partial<ProtoFunction>[]> {
    const fetchAllPages = (
      cursor = '',
      accumulatedFunctions: Partial<ProtoFunction>[] = [],
    ): Observable<Partial<ProtoFunction>[]> => {
      return this.listFunctions({ ...listOptions, cursor }).pipe(
        switchMap((res) => {
          const newAccumulatedFunctions = [...accumulatedFunctions, ...res.functions];

          if (res.hasMore && res.nextCursor) {
            return fetchAllPages(res.nextCursor, newAccumulatedFunctions);
          }

          return of(newAccumulatedFunctions);
        }),
      );
    };

    return fetchAllPages();
  }

  async upsertFunction(aiFunction: Partial<ProtoFunction>): Promise<void> {
    await firstValueFrom(upsertFunctionProto$({ function: aiFunction }));
  }

  async upsertCapability(capability: Partial<Goal>): Promise<string | undefined> {
    if (!capability) {
      throw new Error('Goal is required to upsert capability');
    }
    if (!capability.id) {
      const result = await firstValueFrom(createGoal$({ goal: capability }));
      return result.id;
    }
    await firstValueFrom(upsertGoal$({ goal: capability }));
    return undefined;
  }

  applyAssistantUpdates(request: Partial<ApplyAssistantUpdatesRequest>): Promise<ApplyAssistantUpdatesResponse> {
    return firstValueFrom(applyAssistantUpdatesProto$(request));
  }

  upsertAssistant(request: Partial<UpsertAssistantRequest>): Promise<UpsertAssistantResponse> {
    return firstValueFrom(upsertAssistantProto$(request));
  }

  /**
   * One page of an assistant's published versions, newest first.
   *
   * The version-type filter is stated rather than left unset: unset asks the backend for every type, which once
   * drafts exist would mix them into a list that is meant to show what has been published.
   *
   * There is no total to report — the versions are paged straight off the version index, which counts nothing —
   * so callers page on `hasMore` rather than against a count.
   */
  listPublishedAssistantVersions(
    key: Partial<AssistantKey>,
    options: { cursor?: string; pageSize?: number } = {},
  ): Observable<{ versions: AssistantVersionSummary[]; nextCursor: string; hasMore: boolean }> {
    return listAssistantVersionsProto$({
      key,
      filters: { versionType: VersionType.VERSION_TYPE_PUBLISHED },
      pagingOptions: {
        ...(options.cursor && { cursor: options.cursor }),
        ...(options.pageSize && { pageSize: options.pageSize }),
      },
    }).pipe(
      map((resp) => ({
        versions: resp.versions ?? [],
        nextCursor: resp.metadata?.nextCursor ?? '',
        hasMore: resp.metadata?.hasMore ?? false,
      })),
    );
  }

  /**
   * The assistant as it stood at one of its versions, in the same shape a live read returns — its goals, their
   * functions, and each prompt module's pinned content all hydrated, so rendering the version needs no further
   * reads. In particular it must not be paired with `getMultiPromptModuleVersions`, which answers with each
   * module's *currently deployed* content and would show a historical version with today's prompts.
   *
   * The whole version key is required: the backend rejects a partial one rather than widening the read, so pass
   * back the key the version list gave out.
   */
  getAssistantVersion(version: Partial<AssistantVersionKey>): Observable<Partial<Assistant> | undefined> {
    return getAssistantVersionProto$({ version }).pipe(map((resp) => resp.assistant));
  }

  async deleteAssistant(assistant: Partial<Assistant>): Promise<void> {
    await firstValueFrom(deleteAssistantProto$({ id: assistant.id, namespace: assistant.namespace }));
  }

  listAvailableModels(assistantType?: AssistantType): Observable<Model[]> {
    return listAvailableModels$({
      filters: { type: [ModelType.TYPE_LARGE_LANGUAGE_MODEL], vendor: [], assistantType: assistantType },
    }).pipe(map((resp) => resp.models));
  }
}
